import React from 'react';
import {
  Box, Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  format, isValid, parse,
} from 'date-fns';
import { OutlinedIcons } from '@marageti/z4-lib';

/** Query-param field names shared by trip match and trip request list searches (`createdAt` + `departureDate` ranges). */
export type TripSearchDateRangeKey =
  | 'matchDateFrom'
  | 'matchDateTo'
  | 'departureDateFrom'
  | 'departureDateTo';

const defaultPickerLabels = {
  matchFrom: 'Match Start',
  matchTo: 'Match End',
  departureFrom: 'Departure Start',
  departureTo: 'Departure End',
};

type TripSearchDateRangeFiltersProps = {
  departureDateFrom: string;
  departureDateTo: string;
  matchDateFrom: string;
  matchDateTo: string;
  onChange: (name: TripSearchDateRangeKey, value: string) => void;
  /** Labels on each DatePicker (outlined / notched), same pattern as other filter TextFields. */
  pickerLabels?: Partial<typeof defaultPickerLabels>;
  /**
   * `pairedColumns` — four equal columns with uniform gutters (same as trip-requests grid).
   * `barePickers` — four DatePickers only (no wrapper); parent supplies layout (e.g. CSS grid with Order By on trip requests).
   */
  variant?: 'pairedColumns' | 'barePickers';
};

const minCalendarDate = new Date('2008-01-01T00:00:00');

function parseStoredCalendarDay(dateStr: string): Date | null {
  if (!dateStr.trim()) return null;
  const d = parse(dateStr.trim(), 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
}

/** Cleared or invalid keyboard input yields `null` or an invalid Date; never pass those to `format`. */
function dateToStoredValue(d: Date | null): string {
  if (d == null) return '';
  return isValid(d) ? format(d, 'yyyy-MM-dd') : '';
}

const pickerSlots = {
  openPickerIcon: OutlinedIcons.OutlinedCalendarIcon,
  clearIcon: OutlinedIcons.OutlinedXCircleIcon,
};

const pickerSlotProps = {
  openPickerIcon: { color: 'primary' as const },
  field: {
    clearable: true,
    sx: { '& .clearButton': { opacity: 1 } },
  },
  clearIcon: {
    color: 'primary' as const,
    fontSize: 'small' as const,
  },
  textField: {
    fullWidth: true,
  },
};

const TripSearchDateRangeFilters = ({
  departureDateFrom,
  departureDateTo,
  matchDateFrom,
  matchDateTo,
  onChange,
  pickerLabels: pickerLabelsPartial,
  variant = 'pairedColumns',
}: TripSearchDateRangeFiltersProps) => {
  const pickerLabels = { ...defaultPickerLabels, ...pickerLabelsPartial };
  const today = new Date();
  const matchFrom = parseStoredCalendarDay(matchDateFrom);
  const matchTo = parseStoredCalendarDay(matchDateTo);
  const departureFrom = parseStoredCalendarDay(departureDateFrom);
  const departureTo = parseStoredCalendarDay(departureDateTo);

  const matchFromPicker = (
    <DatePicker
      format="MM/dd/yy"
      label={pickerLabels.matchFrom}
      maxDate={matchTo ?? today}
      minDate={minCalendarDate}
      onChange={(d) => {
        onChange('matchDateFrom', dateToStoredValue(d));
      }}
      slotProps={pickerSlotProps}
      slots={pickerSlots}
      value={matchFrom}
      views={['year', 'month', 'day']}
    />
  );
  const matchToPicker = (
    <DatePicker
      format="MM/dd/yy"
      label={pickerLabels.matchTo}
      maxDate={today}
      minDate={matchFrom ?? minCalendarDate}
      onChange={(d) => {
        onChange('matchDateTo', dateToStoredValue(d));
      }}
      slotProps={pickerSlotProps}
      slots={pickerSlots}
      value={matchTo}
      views={['year', 'month', 'day']}
    />
  );
  const departureFromPicker = (
    <DatePicker
      format="MM/dd/yy"
      label={pickerLabels.departureFrom}
      maxDate={departureTo ?? undefined}
      minDate={minCalendarDate}
      onChange={(d) => {
        onChange('departureDateFrom', dateToStoredValue(d));
      }}
      slotProps={pickerSlotProps}
      slots={pickerSlots}
      value={departureFrom}
      views={['year', 'month', 'day']}
    />
  );
  const departureToPicker = (
    <DatePicker
      format="MM/dd/yy"
      label={pickerLabels.departureTo}
      minDate={departureFrom ?? minCalendarDate}
      onChange={(d) => {
        onChange('departureDateTo', dateToStoredValue(d));
      }}
      slotProps={pickerSlotProps}
      slots={pickerSlots}
      value={departureTo}
      views={['year', 'month', 'day']}
    />
  );

  if (variant === 'barePickers') {
    const wrap = (node: React.ReactElement, key: string) => (
      <Box key={key} sx={{ minWidth: 0 }}>{node}</Box>
    );
    return (
      <>
        {wrap(matchFromPicker, 'matchFrom')}
        {wrap(matchToPicker, 'matchTo')}
        {wrap(departureFromPicker, 'departureFrom')}
        {wrap(departureToPicker, 'departureTo')}
      </>
    );
  }

  return (
    <Grid
      container
      columnSpacing={{ mobile: 0, desktop: 2 }}
      mb={{ mobile: 2, desktop: 0 }}
      rowSpacing={{ mobile: 2, desktop: 0 }}
    >
      <Grid item mobile={12} desktop={3} sx={{ minWidth: 0 }}>
        {matchFromPicker}
      </Grid>
      <Grid item mobile={12} desktop={3} sx={{ minWidth: 0 }}>
        {matchToPicker}
      </Grid>
      <Grid item mobile={12} desktop={3} sx={{ minWidth: 0 }}>
        {departureFromPicker}
      </Grid>
      <Grid item mobile={12} desktop={3} sx={{ minWidth: 0 }}>
        {departureToPicker}
      </Grid>
    </Grid>
  );
};

export default TripSearchDateRangeFilters;
